<script setup lang="ts">
import type { ResultsAlmaBannerProperties } from '@optionway/models';

import { CreditCard } from 'lucide-vue-next';

// What the partner advertises, not what its Alma contract allows: with no amount to submit, this page
// cannot ask the eligibility API. A partner that names no figure says nothing rather than guess one.
const properties = defineProps<ResultsAlmaBannerProperties>();

const { $appConfig } = useNuxtApp();
const config = $appConfig.app?.components?.resultsAlmaBanner;

const componentConfig = computed<ResultsAlmaBannerProperties>(() => ({
  installmentsCount: properties.installmentsCount ?? config?.installmentsCount,
  minAmountInEuro: properties.minAmountInEuro ?? config?.minAmountInEuro,
  maxAmountInEuro: properties.maxAmountInEuro ?? config?.maxAmountInEuro,
}));
</script>

<template>
  <div class="flex items-center gap-2 rounded-xl border border-orange-alma bg-background px-4 py-2.5">
    <CreditCard
      class="size-4 shrink-0 text-text-primary-900"
      aria-hidden="true"
    />
    <p class="flex flex-wrap items-center gap-2 text-xs font-normal text-text-primary-900 md:text-sm">
      <i18n-t
        scope="global"
        keypath="components.results.almaBanner.payInInstalments"
        tag="span"
        class="flex flex-wrap items-center gap-1"
      >
        <template #installments>{{ componentConfig.installmentsCount }}</template>
        <template #logo>
          <SvgoCommonAlmaLogo
            class="h-2.5 w-auto shrink-0 md:h-3"
            filled
            :font-controlled="false"
            role="img"
            :aria-label="$t('components.results.almaBanner.almaLogo')"
          />
        </template>
      </i18n-t>
      <!-- Alma settles in euros only, so these bounds ignore the site currency selector. -->
      <template v-if="componentConfig.minAmountInEuro !== undefined && componentConfig.maxAmountInEuro !== undefined">
        <span
          class="h-3 w-px bg-text-secondary-400"
          aria-hidden="true"
        />
        <i18n-t
          scope="global"
          keypath="components.results.almaBanner.availableRange"
          tag="span"
          class="translate-y-px text-xs font-light text-text-secondary-600"
        >
          <template #min>{{ usePriceFormat(componentConfig.minAmountInEuro, 1, 0, 0, 'EUR') }}</template>
          <template #max>{{ usePriceFormat(componentConfig.maxAmountInEuro, 1, 0, 0, 'EUR') }}</template>
        </i18n-t>
      </template>
    </p>
  </div>
</template>
